import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { menuItemsDef, settingsItemsDef } from "../../navigation/navDefinitions";
import { topbarHelpItems } from "../../navigation/topbarHelpDefinitions";
import { guideContentByKey } from "./guideContent";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const MAIN_MATCH_ROUTES_BY_RES = {
  chat: ["/", "/chat", "/popup-call"],
  surveys: ["/surveys"],
};

const SETTINGS_MATCH_ROUTES_BY_RES = {
  users: ["/settings/users", "/user-details", "/users/"],
  chatbot: ["/settings/chatbot", "/users/:id/bot"],
  payment_methods: ["/settings/payment-methods", "/ecom/payment-methods"],
};

function buildMenuOrder(defs, matchByRes) {
  return defs.map((d) => ({
    key: d.res,
    textKey: d.textKey,
    label: d.label,
    matchRoutes: matchByRes?.[d.res] || [d.link],
  }));
}

const MENU_MAIN_ORDER = buildMenuOrder(menuItemsDef, MAIN_MATCH_ROUTES_BY_RES);
const MENU_SETTINGS_ORDER = buildMenuOrder(settingsItemsDef, SETTINGS_MATCH_ROUTES_BY_RES);

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    if (row.length === 1 && row[0] === "") return;
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (c === `"` && next === `"`) {
        field += `"`;
        i += 1;
      } else if (c === `"`) {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === `"`) {
      inQuotes = true;
    } else if (c === ",") {
      pushField();
    } else if (c === "\n") {
      pushField();
      pushRow();
    } else if (c === "\r") {
      // ignore
    } else {
      field += c;
    }
  }

  pushField();
  pushRow();

  const [header, ...body] = rows;
  if (!header || header.length < 3) return [];

  const norm = (s) => String(s || "").trim();
  const headerIdx = {};
  header.forEach((h, idx) => {
    headerIdx[norm(h)] = idx;
  });

  const get = (cols, r) => {
    for (const col of cols) {
      const j = headerIdx[col];
      if (j != null) return norm(r[j]);
    }
    return "";
  };

  return body
    .map((r) => ({
      surface: get(["surface"], r),
      area: get(["area"], r),
      page: get(["page"], r),
      route: get(["route"], r),
      feature: get(["feature"], r),
      variants: get(["variants"], r),
      permissions: get(["permissions"], r),
      segment: get(["segment"], r),
      notes: get(["notes"], r),
    }))
    .filter((x) => x.surface || x.area || x.page || x.route || x.feature);
}

function normalizeForSearch(v) {
  return String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function groupBy(items, keyFn) {
  const map = new Map();
  items.forEach((it) => {
    const k = keyFn(it) || "—";
    const list = map.get(k) || [];
    list.push(it);
    map.set(k, list);
  });
  return map;
}

function dashboardTabAndTitle(feature) {
  const f = String(feature || "").trim();
  if (!f.toLowerCase().startsWith("aba:")) return null;
  // format: "Aba: <tab> — <title>"
  const rest = f.slice(4).trim();
  const parts = rest.split("—").map((x) => x.trim());
  const tab = parts[0] || "—";
  const title = parts.slice(1).join(" — ").trim() || "—";
  return { tab, title };
}

function normalizeDashboardTabName(tab) {
  const t = String(tab || "").trim();
  if (!t) return "—";
  // "Visão geral (Atendimento)" -> "Visão geral"
  const noParen = t.replace(/\s*\([^)]*\)\s*$/g, "").trim();
  // keep legacy CSV naming but normalize for grouping
  if (noParen.toLowerCase() === "passagens") return "Passagens rodoviárias";
  if (noParen.toLowerCase() === "passagens (segmento)") return "Passagens rodoviárias";
  return noParen;
}

function splitSemicolonList(v) {
  return String(v || "")
    .split(";")
    .map((x) => x.trim())
    .filter(Boolean);
}

function stripPrefixCI(value, prefix) {
  const v = String(value || "");
  const p = String(prefix || "");
  if (!v || !p) return v.trim();
  const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^${escaped}\\s*`, "i");
  return v.replace(re, "").trim();
}

function labelSurface(s) {
  const k = String(s || "").trim();
  const map = {
    app: "Dentro do Xpro (você logado)",
    public_ecom: "E-commerce (para seus clientes)",
    public_websites: "Sites públicos (para seus clientes)",
    public_surveys: "Pesquisas (link público)",
    platform_console: "Console da plataforma (master)",
    voice_sms: "Telefonia e SMS (infraestrutura)",
  };
  return map[k] || (k ? k : "—");
}

function copyToClipboard(text) {
  try {
    navigator.clipboard?.writeText?.(text);
  } catch (_) {}
}

function pageKeyFromItem(it) {
  const page = it.page || "—";
  const route = it.route || "—";
  return `${page}|||${route}`;
}

function routeLooksLikeAppPath(route) {
  const r = String(route || "").trim();
  return r.startsWith("/");
}

function matchByPrefixes(route, prefixes) {
  const r = String(route || "").trim();
  if (!r) return false;
  return prefixes.some((p) => {
    const pref = String(p || "").trim();
    if (!pref) return false;
    // special placeholders we use above
    // IMPORTANT: "/" must match only the root route, otherwise it will swallow every app path.
    if (pref === "/") return r === "/";
    if (pref === "/users/:id/bot") return r.includes("/users/") && r.includes("/bot");
    if (pref === "/users/") return r.includes("/users/");
    if (pref === "/webhooks/") return r.includes("/webhooks/");
    if (pref === "/channels/") return r.includes("/channels/");
    if (pref === "/telephony/") return r.includes("/telephony/");
    if (pref === "/public/surveys") return r.includes("/public/surveys");
    if (pref === "/public/websites") return r.includes("/public/websites");
    if (pref === "/ecom/payment-methods") return r.includes("/ecom/payment-methods");
    return r === pref || r.startsWith(`${pref}/`) || r.startsWith(pref);
  });
}

function bucketForItem(it) {
  // Priorização: Settings primeiro, depois Main; se não bater, cai em “Outros”
  const route = it.route || "";

  for (const def of MENU_SETTINGS_ORDER) {
    if (matchByPrefixes(route, def.matchRoutes)) return { section: "settings", key: def.key };
  }
  for (const def of MENU_MAIN_ORDER) {
    if (matchByPrefixes(route, def.matchRoutes)) return { section: "main", key: def.key };
  }

  // Se não é rota de app (api_only, config, etc.), tenta inferir pela área/página
  const area = String(it.area || "").toLowerCase();
  const page = String(it.page || "").toLowerCase();
  if (area === "settings" && page.includes("channels")) return { section: "settings", key: "channels" };
  if (area === "settings" && page.includes("billing")) return { section: "settings", key: "billing" };
  if (String(it.surface || "") === "voice_sms") return { section: "settings", key: "channels" };

  return { section: "other", key: "other" };
}

function splitList(s) {
  return String(s || "")
    .split(/[;,]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeSeg(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export default function HelpPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("app");

  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [showExplanations, setShowExplanations] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch("/feature-map.csv", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) setRaw(t || "");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Não foi possível carregar o mapa de features agora. Tente atualizar a página.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allItems = useMemo(() => parseCsv(raw), [raw]);

  const filteredByQuery = useMemo(() => {
    const q = normalizeForSearch(query);
    if (!q) return allItems;
    return allItems.filter((it) => {
      const hay = [
        it.surface,
        it.area,
        it.page,
        it.route,
        it.feature,
        it.variants,
        it.permissions,
        it.segment,
        it.notes,
      ]
        .map(normalizeForSearch)
        .join(" | ");
      return hay.includes(q);
    });
  }, [allItems, query]);

  // Inventário completo: sem filtro por acesso/segmento (apenas busca)
  const filtered = filteredByQuery;

  const grouped = useMemo(() => {
    const withBucket = filtered.map((it) => ({ ...it, __bucket: bucketForItem(it) }));
    const main = withBucket.filter((x) => x.__bucket.section === "main");
    const settings = withBucket.filter((x) => x.__bucket.section === "settings");
    const other = withBucket.filter((x) => x.__bucket.section === "other");

    const buildItemGroup = (items) => {
      const byPage = groupBy(items, (x) => pageKeyFromItem(x));
      const pages = Array.from(byPage.entries()).sort((a, b) => a[0].localeCompare(b[0]));
      return pages.map(([key, pageItems]) => {
        const [page, route] = String(key).split("|||");
        return { page, route, count: pageItems.length, items: pageItems };
      });
    };

    const buildOrderedGroup = (defs, items) => {
      return defs
        .map((def) => {
          const inBucket = items.filter((x) => x.__bucket.key === def.key);
          return {
            key: def.key,
            label: def.label || t(def.textKey),
            count: inBucket.length,
            items: inBucket,
            pages: buildItemGroup(inBucket),
          };
        });
    };

    const mainGroups = buildOrderedGroup(MENU_MAIN_ORDER, main);
    const settingsGroups = buildOrderedGroup(MENU_SETTINGS_ORDER, settings);

    // “Outros”: mantém agrupamento por surface/area como fallback
    const bySurface = groupBy(other, (x) => x.surface);
    const surfaces = Array.from(bySurface.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const otherGroups = surfaces.map(([surfaceKey, surfaceItems]) => {
      const byArea = groupBy(surfaceItems, (x) => x.area);
      const areas = Array.from(byArea.entries()).sort((a, b) => a[0].localeCompare(b[0]));
      return {
        surfaceKey,
        surfaceLabel: labelSurface(surfaceKey),
        count: surfaceItems.length,
        areas: areas.map(([areaKey, areaItems]) => ({
          areaKey,
          count: areaItems.length,
          pages: buildItemGroup(areaItems),
        })),
      };
    });

    return { mainGroups, settingsGroups, otherGroups };
  }, [filtered, t]);

  const totalFeatures = filtered.length;
  const totalAll = allItems.length;

  const topbarModel = useMemo(() => {
    // Inventário completo: mostra tudo do Topbar
    return topbarHelpItems.map((it) => {
      if (it.kind !== "group" || !Array.isArray(it.children)) return it;
      return { ...it, children: it.children };
    });
  }, []);

  const quickStart = useMemo(() => {
    // “Comece por aqui” sempre visível (inventário completo)
    return [
      { key: "chat", label: "Atendimento (Chat)", route: "/chat" },
      { key: "clients", label: "Clientes", route: "/clients" },
      { key: "channels", label: "Canais", route: "/settings/channels" },
      { key: "users", label: "Usuários", route: "/settings/users" },
      { key: "products", label: "Produtos", route: "/products" },
      { key: "billing", label: "Plano e faturamento", route: "/settings/billing" },
    ];
  }, []);

  const renderPage = (p, keyPrefix) => {
    const routeChip =
      p.route && p.route !== "—" && routeLooksLikeAppPath(p.route) ? (
        <Chip
          size="small"
          label={p.route}
          onClick={() => copyToClipboard(p.route)}
          variant="outlined"
          sx={{ fontFamily: "monospace" }}
        />
      ) : null;

    return (
      <Accordion key={`${keyPrefix}-${p.page}-${p.route}`} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
            <Typography sx={{ fontWeight: 700 }}>{p.page || "—"}</Typography>
            <Chip size="small" label={`${p.count}`} variant="outlined" />
            <Box sx={{ flex: 1 }} />
            {routeChip}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {p.items.map((it, idx) => (
              <ListItem key={`${keyPrefix}-${p.page}-${it.feature}-${idx}`} disableGutters sx={{ py: 0.5 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                      {it.feature || "—"}
                    </Typography>
                  }
                  secondary={
                    <>
                      {(it.variants || it.notes) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                          {it.variants ? it.variants : it.notes}
                        </Typography>
                      )}
                      {(it.permissions || it.segment) && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          {it.permissions && <Chip size="small" label={`Acesso: ${it.permissions}`} />}
                          {it.segment && <Chip size="small" variant="outlined" label={`Disponível em: ${it.segment}`} />}
                        </Stack>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDashboardTree = (items) => {
    const dash = items.filter((it) => String(it.route || "").trim() === "/dashboard");
    const geral = dash.filter((it) => !String(it.feature || "").toLowerCase().startsWith("aba:"));
    const tabs = dash
      .map((it) => ({ it, parsed: dashboardTabAndTitle(it.feature) }))
      .filter((x) => x.parsed)
      .map((x) => ({
        ...x.parsed,
        tab: normalizeDashboardTabName(x.parsed.tab),
        it: x.it,
      }));

    const tabOrder = ["Visão geral", "Clientes", "Produtos", "Vendas", "Passagens rodoviárias", "Pesquisas"];
    const tabsByName = groupBy(tabs, (x) => x.tab);

    const renderFeatureRow = (it, keyPrefix) => {
      return (
        <ListItem key={`${keyPrefix}-${it.feature}`} disableGutters sx={{ py: 0.5 }}>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {it.feature || "—"}
              </Typography>
            }
            secondary={
              <>
                {(it.notes || it.variants) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                    {it.notes ? it.notes : it.variants}
                  </Typography>
                )}
              </>
            }
          />
        </ListItem>
      );
    };

    const renderTabItem = (x, keyPrefix) => {
      const title = x.title;
      const isKpis = String(title).toLowerCase() === "kpis";
      if (isKpis) {
        const cards = splitSemicolonList(x.it.variants);
        return (
          <Box key={`${keyPrefix}-${x.tab}-kpis`} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Cards (KPIs)</Typography>
            {cards.length === 0 ? (
              <Alert severity="info">KPIs sem cards listados no inventário ainda.</Alert>
            ) : (
              <List dense disablePadding>
                {cards.map((c) => (
                  <ListItem key={`${keyPrefix}-${x.tab}-kpi-${c}`} disableGutters sx={{ py: 0.4 }}>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600 }}>{c}</Typography>}
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, lineHeight: 1.5 }}>
                          {x.it.notes || "Mostra um indicador resumido do período selecionado para apoiar decisões rápidas."}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );
      }

      return (
        <ListItem key={`${keyPrefix}-${x.tab}-${title}`} disableGutters sx={{ py: 0.5 }}>
          <ListItemText
            primary={<Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{title || "—"}</Typography>}
            secondary={
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                {x.it.variants || x.it.notes || "—"}
              </Typography>
            }
          />
        </ListItem>
      );
    };

    return (
      <Stack spacing={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>Geral</Typography>
              <Chip size="small" label={`${geral.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {showExplanations && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                Tudo que afeta o dashboard inteiro: período (datas), exportação e recursos globais.
              </Typography>
            )}
            {geral.length === 0 ? (
              <Alert severity="info">Sem itens globais mapeados para o Dashboard ainda.</Alert>
            ) : (
              <List dense disablePadding>
                {geral.map((it) => renderFeatureRow(it, "dash-geral"))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>Abas</Typography>
              <Chip size="small" label={`${tabs.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {tabOrder.map((tabName) => {
                const list = tabsByName.get(tabName) || [];
                return (
                  <Accordion key={`dash-tab-${tabName}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{tabName}</Typography>
                        <Chip size="small" label={`${list.length}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                          Cards e gráficos desta aba para o período selecionado.
                        </Typography>
                      )}
                      {list.length === 0 ? (
                        <Alert severity="info">Nenhum card/gráfico mapeado para esta aba ainda.</Alert>
                      ) : (
                        <List dense disablePadding>
                          {list.map((x) => renderTabItem(x, `dash-${tabName}`))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  const renderL2PrefixedTree = ({
    items,
    route,
    prefix,
    generalTitle = "Geral",
    generalEmptyText = "Sem itens gerais mapeados ainda.",
    generalHelpText,
    childrenTitle,
    emptyHintPrefixLabel,
    order = [],
  }) => {
    const all = items.filter((it) => String(it.route || "").trim() === route);
    const prefLc = String(prefix || "").toLowerCase();
    const children = all
      .filter((it) => String(it.feature || "").toLowerCase().startsWith(prefLc))
      .map((it) => ({ name: stripPrefixCI(it.feature, prefix) || "—", it }));
    const geral = all.filter((it) => !String(it.feature || "").toLowerCase().startsWith(prefLc));

    const byName = new Map();
    children.forEach((c) => byName.set(c.name, c.it));
    const ordered = [
      ...order.filter((n) => byName.has(n)).map((n) => ({ name: n, it: byName.get(n) })),
      ...children
        .filter((c) => !order.includes(c.name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ name: c.name, it: c.it })),
    ];

    const renderGeneralRow = (it, keyPrefix) => (
      <ListItem key={`${keyPrefix}-${it.feature}`} disableGutters sx={{ py: 0.5 }}>
        <ListItemText
          primary={<Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{it.feature || "—"}</Typography>}
          secondary={
            <>
              {(it.variants || it.notes) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                  {it.variants ? it.variants : it.notes}
                </Typography>
              )}
              {it.permissions && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <Chip size="small" label={`Acesso: ${it.permissions}`} />
                </Stack>
              )}
            </>
          }
        />
      </ListItem>
    );

    return (
      <Stack spacing={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>{generalTitle}</Typography>
              <Chip size="small" label={`${geral.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {showExplanations && generalHelpText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                {generalHelpText}
              </Typography>
            )}
            {geral.length === 0 ? (
              <Alert severity="info">{generalEmptyText}</Alert>
            ) : (
              <List dense disablePadding>
                {geral.map((it) => renderGeneralRow(it, `l2-${route}-geral`))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>{childrenTitle}</Typography>
              <Chip size="small" label={`${ordered.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {ordered.map(({ name, it }) => (
                <Accordion key={`l2-${route}-${prefix}-${name}`} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
                      <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
                      <Box sx={{ flex: 1 }} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {it.variants || "—"}
                    </Typography>
                    {it.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        {it.notes}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
              {ordered.length === 0 && (
                <Alert severity="info">
                  Nenhum item mapeado ainda. Adicione linhas no inventário com prefixo <strong>{emptyHintPrefixLabel}</strong>.
                </Alert>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  const renderPaymentMethodsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/payment-methods",
      prefix: "Provedor:",
      childrenTitle: "Tipos / provedores",
      emptyHintPrefixLabel: "Provedor:",
      order: [
        "Default",
        "PIX Buspay",
        "Pagar.me",
        "Cielo",
        "Pague Fácil",
        "Dinheiro",
        "Mercado Pago",
        "Xpay",
        "PIX Itaú",
        "Rede",
        "Adyen",
        "AppMax — link / checkout",
        "AppMax — PIX (API)",
        "AppMax — cartão (API)",
      ],
      generalHelpText: "Cadastro, ativação e mapeamentos que valem para qualquer forma de pagamento.",
      generalEmptyText: "Sem itens gerais mapeados para Formas de pagamento ainda.",
    });

  const renderDeliveryMethodsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/delivery-methods",
      prefix: "Método:",
      childrenTitle: "Tipos / métodos",
      emptyHintPrefixLabel: "Método:",
      order: ["Correios", "Vuxx", "Retirada", "Gestão de Envios", "Fallback por fator/peso"],
      generalHelpText: "Cadastro, ativação e regras gerais de entrega (elegibilidade por CEP/valor/peso e prazos).",
      generalEmptyText: "Sem itens gerais mapeados para Métodos de entrega ainda.",
    });

  const renderChannelsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/channels",
      prefix: "Provedor:",
      childrenTitle: "Tipos / provedores",
      emptyHintPrefixLabel: "Provedor:",
      order: [
        "WhatsApp — Meta",
        "WhatsApp — Gupshup",
        "WhatsApp — API Fácil",
        "Meta — Instagram/Messenger",
        "Email — AWS SES",
        "Website (widget/site)",
        "Telefonia — Twilio Voice",
        "SMS — Twilio",
        "SMS — Clickatell",
      ],
      generalHelpText: "Cadastro de canais, conexão, status e padrões (tipos/subtipos por canal).",
      generalEmptyText: "Sem itens gerais mapeados para Canais ainda.",
    });

  const renderIntegrationsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/integrations",
      prefix: "Provedor:",
      childrenTitle: "Tipos / integrações",
      emptyHintPrefixLabel: "Provedor:",
      order: ["Passagens rodoviárias (TotalBus)", "URA gateway (passagens)"],
      generalHelpText: "Cadastro de integrações, testes de conexão e configurações por domínio (ex.: passagens).",
      generalEmptyText: "Sem itens gerais mapeados para Integrações ainda.",
    });

  const renderAntifraudTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/antifraud-methods",
      prefix: "Provedor:",
      childrenTitle: "Tipos / provedores",
      emptyHintPrefixLabel: "Provedor:",
      order: ["Konduto", "ClearSale", "FControl"],
      generalHelpText: "Cadastro de métodos antifraude e mapeamento de decisões para Tipos/Subtipos.",
      generalEmptyText: "Sem itens gerais mapeados para Antifraude ainda.",
    });

  return (
    <Box sx={{ pt: 1, pb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Guia do produto
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
          Uma visão organizada de <strong>todas as páginas</strong> e do que você consegue fazer em cada uma.
          Perfeito para tirar dúvidas, treinar time e encontrar rapidamente uma configuração.
        </Typography>
      </Box>

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1.5}
        sx={{ mb: 2, alignItems: isMobile ? "stretch" : "center" }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Buscar por "pagamento", "/settings", "chat", "agente", "whatsapp"...'
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showExplanations}
              onChange={(e) => setShowExplanations(e.target.checked)}
              color="primary"
            />
          }
          label="Mostrar explicações (modo guia)"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`${totalFeatures} itens exibidos`}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${totalAll} itens no guia`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`Atualização automática`}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
            Dica: se algum item do menu estiver com 0 funcionalidades, ele ainda não foi mapeado no inventário (feature-map.csv).
          </Typography>
        </Stack>
      </Box>

      {quickStart.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>Comece por aqui</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {quickStart.map((c) => (
              <Button
                key={c.key}
                component={RouterLink}
                to={c.route}
                variant="contained"
                size="small"
              >
                {c.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}{" "}
          <Link component="button" onClick={() => window.location.reload()}>
            Atualizar
          </Link>
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Carregando páginas e funcionalidades…
          </Typography>
        </Box>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Alert severity="info">
          Não encontrei nada com esse termo. Tente buscar por uma palavra mais curta (ex.: &quot;pag&quot;, &quot;chat&quot;, &quot;settings&quot;).
        </Alert>
      )}

      {!loading && (grouped.mainGroups.length > 0 || grouped.settingsGroups.length > 0 || grouped.otherGroups.length > 0) && (
        <Box>
          {/* Menu principal */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Menu principal</Typography>
                <Chip size="small" label={`${grouped.mainGroups.reduce((a, g) => a + g.count, 0)}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {grouped.mainGroups.map((g) => (
                  <Accordion key={`main-${g.key}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{g.label}</Typography>
                        <Chip size="small" label={`${g.count}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && guideContentByKey?.main?.[g.key] && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {guideContentByKey.main[g.key].title}
                          </Typography>
                          {guideContentByKey.main[g.key].what && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                              {guideContentByKey.main[g.key].what}
                            </Typography>
                          )}
                          {Array.isArray(guideContentByKey.main[g.key].steps) && guideContentByKey.main[g.key].steps.length > 0 && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Como usar
                              </Typography>
                              <Box component="ol" sx={{ m: 0.5, pl: 2.5 }}>
                                {guideContentByKey.main[g.key].steps.map((s) => (
                                  <li key={s}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                      {s}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Stack spacing={0.75}>
                        {g.key === "dashboard" ? (
                          renderDashboardTree(g.items || [])
                        ) : g.pages.length === 0 ? (
                          <Alert severity="info">
                            Nenhuma funcionalidade mapeada para este item ainda.
                          </Alert>
                        ) : (
                          g.pages.map((p) => renderPage(p, `main-${g.key}`))
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Configurações */}
          <Accordion defaultExpanded sx={{ mt: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Configurações</Typography>
                <Chip size="small" label={`${grouped.settingsGroups.reduce((a, g) => a + g.count, 0)}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {grouped.settingsGroups.map((g) => (
                  <Accordion key={`settings-${g.key}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{g.label}</Typography>
                        <Chip size="small" label={`${g.count}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && guideContentByKey?.settings?.[g.key] && (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {guideContentByKey.settings[g.key].title}
                          </Typography>
                          {guideContentByKey.settings[g.key].what && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                              {guideContentByKey.settings[g.key].what}
                            </Typography>
                          )}
                          {Array.isArray(guideContentByKey.settings[g.key].steps) && guideContentByKey.settings[g.key].steps.length > 0 && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Como usar
                              </Typography>
                              <Box component="ol" sx={{ m: 0.5, pl: 2.5 }}>
                                {guideContentByKey.settings[g.key].steps.map((s) => (
                                  <li key={s}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                      {s}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Stack spacing={0.75}>
                        {g.key === "payment_methods" ? (
                          renderPaymentMethodsTree(g.items || [])
                        ) : g.key === "delivery_methods" ? (
                          renderDeliveryMethodsTree(g.items || [])
                        ) : g.key === "channels" ? (
                          renderChannelsTree(g.items || [])
                        ) : g.key === "integrations" ? (
                          renderIntegrationsTree(g.items || [])
                        ) : g.key === "antifraud_methods" ? (
                          renderAntifraudTree(g.items || [])
                        ) : g.pages.length === 0 ? (
                          <Alert severity="info">
                            Nenhuma funcionalidade mapeada para este item ainda.
                          </Alert>
                        ) : (
                          g.pages.map((p) => renderPage(p, `settings-${g.key}`))
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Topo (Topbar) */}
          <Accordion defaultExpanded sx={{ mt: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Topo (barra superior)</Typography>
                <Chip size="small" label={`${topbarModel.length}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {topbarModel.map((it) => (
                  <Accordion key={`top-${it.key}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{it.label}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {guideContentByKey?.topbar?.[it.key] && (
                        <Box sx={{ mb: 1 }}>
                          {guideContentByKey.topbar[it.key].what && (
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                              {guideContentByKey.topbar[it.key].what}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {it.kind === "group" && Array.isArray(it.children) && it.children.length > 0 ? (
                        <Stack spacing={0.75}>
                          {it.children.map((ch) => (
                            <Box
                              key={`top-${it.key}-${ch.key}`}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1.25,
                                bgcolor: "background.paper",
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontWeight: 600 }}>{ch.label}</Typography>
                                <Box sx={{ flex: 1 }} />
                                {ch.kind === "route" && ch.route && (
                                  <Chip
                                    size="small"
                                    label={ch.route}
                                    onClick={() => copyToClipboard(ch.route)}
                                    variant="outlined"
                                    sx={{ fontFamily: "monospace" }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        it.kind === "route" &&
                        it.route && (
                          <Chip
                            size="small"
                            label={it.route}
                            onClick={() => copyToClipboard(it.route)}
                            variant="outlined"
                            sx={{ fontFamily: "monospace" }}
                          />
                        )
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
}

